package com.wintermar.inspection.core.ui.theme.color.model

import androidx.compose.ui.graphics.Color

data class ThemeColor(
    val bg: BgThemeColor,
    val border: BorderThemeColor,
    val icon: IconThemeColor,
    val surface: SurfaceThemeColor,
    val text: TextThemeColor
)

data class BgThemeColor(
    val inverse: Color,
    val normal: Color
)

data class BorderThemeColor(
    val bold: Color,
    val default: Color,
    val error: Color,
    val info: Color,
    val subtle: Color,
    val success: Color,
    val warning: Color,
    val brand: BorderBrandThemeColor,
    val input: BorderInputThemeColor
)

data class BorderBrandThemeColor(val primary: Color, val secondary: Color)
data class BorderInputThemeColor(
    val default: Color,
    val disabled: Color,
    val error: Color,
    val focused: Color,
    val hover: Color,
    val success: Color
)

data class IconThemeColor(
    val brand: Color,
    val default: Color,
    val disabled: Color,
    val inverse: Color,
    val onBrand: Color,
    val subtle: Color,
    val subtlest: Color,
    val error: IconSemanticThemeColor,
    val info: IconSemanticThemeColor,
    val success: IconSemanticThemeColor,
    val warning: IconSemanticThemeColor
)

data class IconSemanticThemeColor(val bolder: Color, val default: Color, val onColor: Color)

data class SurfaceThemeColor(
    val lowered: Color,
    val normal: Color,
    val raised: Color,
    val accent: SurfaceAccentThemeColor,
    val brand: SurfaceBrandThemeColor,
    val gray: SurfaceGrayThemeColor,
    val input: SurfaceInputThemeColor,
    val nav: SurfaceNavThemeColor,
    val neutral: SurfaceNeutralThemeColor,
    val semantic: SurfaceSemanticThemeColor
)

data class SurfaceNavThemeColor(val bg: Color, val bgHover: Color, val bgSelected: Color)
data class SurfaceInputThemeColor(
    val active: Color,
    val default: Color,
    val disabled: Color,
    val hover: Color
)

data class SurfaceGrayThemeColor(
    val bold: SurfaceStateThemeColor,
    val subtle: SurfaceStateThemeColor
)

data class SurfaceBrandThemeColor(
    val primary: SurfaceBrandPrimaryThemeColor,
    val primarySubtle: SurfaceBrandPrimaryThemeColor,
    val secondary: SurfaceBrandPrimaryThemeColor
)

data class SurfaceBrandPrimaryThemeColor(
    val active: Color,
    val default: Color,
    val disabled: Color,
    val hover: Color,
    val pressed: Color
)

data class SurfaceStateThemeColor(val default: Color, val hover: Color, val pressed: Color)

data class SurfaceAccentThemeColor(
    val blue: AccentGroupThemeColor,
    val lime: AccentGroupThemeColor,
    val magenta: AccentGroupThemeColor,
    val orange: AccentGroupThemeColor,
    val purple: AccentGroupThemeColor,
    val red: AccentGroupThemeColor,
    val teal: AccentGroupThemeColor,
    val yellow: AccentGroupThemeColor,
    val green: AccentGroupThemeColor
)

data class AccentGroupThemeColor(
    val bold: AccentToneThemeColor,
    val medium: AccentToneThemeColor,
    val subtle: AccentToneThemeColor
)

data class AccentToneThemeColor(val active: Color, val default: Color, val hover: Color)

data class SurfaceNeutralThemeColor(
    val bold: SurfaceStateThemeColor,
    val bolder: SurfaceStateThemeColor,
    val subtle: SurfaceStateThemeColor,
    val subtlest: SurfaceStateThemeColor
)

data class SurfaceSemanticThemeColor(
    val error: SemanticToneThemeColor,
    val info: SemanticToneThemeColor,
    val success: SemanticToneThemeColor,
    val warning: SemanticToneThemeColor
)

data class SemanticToneThemeColor(
    val bold: SemanticStateThemeColor,
    val subtle: SemanticStateThemeColor
)

data class SemanticStateThemeColor(
    val active: Color,
    val default: Color,
    val hover: Color,
    val pressed: Color? = null
)

data class TextThemeColor(
    val brand: Color,
    val default: Color,
    val disabled: Color,
    val inverse: Color,
    val onBrand: Color,
    val subtle: Color,
    val subtlest: Color,
    val error: TextSemanticThemeColor,
    val info: TextSemanticThemeColor,
    val link: TextLinkThemeColor,
    val success: TextSemanticThemeColor,
    val warning: TextSemanticThemeColor
)

data class TextSemanticThemeColor(val bolder: Color, val default: Color, val on: Color)
data class TextLinkThemeColor(
    val error: Color,
    val info: Color,
    val primary: Color,
    val success: Color,
    val visited: Color,
    val warning: Color
)
